import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Plus } from "lucide-react";
import {
  Box,
  CircularProgress,
  MenuItem,
  TextField,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { ApplicationRoles, getOrganizationUser } from "../../Apis/Organization-User";
import { fetchUserTypes } from "../../Apis/UserTypes";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import usePermissionDataStore from "../../Zustand/Store/usePermissionDataStore";
import axios from "axios";
import { MAIN_URL, getApiHeaders } from "../../Configurations/Urls";
import {
  resolveOrganizationFromUserData,
  resolveOrganizationId,
  resolveOrganizationUserId,
} from "../../Utils/organizationSession";
import NotAllowed from "../../Exceptions/NotAllowed";
import TableDataGeneric from "../../Configurations/TableDataGeneric";
import { operatorRequiresValue } from "../../Components/filters/operators";
import {
  extractToggleStatusError,
  withUserToggleGuard,
} from "../../Utils/organizationUserToggle";
import {
  USER_DEFAULT_COLUMNS,
  RESOURCE_ROLE_COLUMNS,
  USERS_DATAGRID_KEY,
  mapSavedUserColumns,
  mapUserRowForDisplay,
  mapResourceUserRowForDisplay,
  resolveUserApiField,
} from "./userListConstants";
import {
  buildUsersSearchParamsForRole,
  resolveActiveResourceRole,
} from "./resourceRoleConfig";

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "application_user_full_name",
  sortOrder: "asc",
};

const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue
            .split(",")
            .map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({
        field: resolveUserApiField(field),
        operator,
        value: filterValue,
      });
    }
  });

  return filters;
};

function Users({ resourceRoleConfig = null }) {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [search, setSearch] = useState("");
  const { userData } = useAuthStore();
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(USER_DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);

  const org = useMemo(
    () => resolveOrganizationFromUserData(userData),
    [userData]
  );
  const organizationId = useMemo(
    () => resolveOrganizationId(userData),
    [userData]
  );
  const [filterStatus, setFilterStatus] = useState("all");
  const { Permission, setPermission } = usePermissionDataStore();
  const [showNotAllowed, setShowNotAllowed] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [userTypeOptions, setUserTypeOptions] = useState([]);
  const [userRoleOptions, setUserRoleOptions] = useState([]);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);
  const hasInitializedRoleFilter = useRef(false);
  const serverSearchDebounceRef = useRef(null);
  const hasLoadedUsersOnceRef = useRef(false);

  const activeResourceRole = useMemo(
    () => resolveActiveResourceRole(searchParams, resourceRoleConfig),
    [searchParams, resourceRoleConfig]
  );

  const pageSubtitle = activeResourceRole?.title || "User management";
  const pageCountLabel = activeResourceRole?.countLabel || "users";
  const tableHeading = activeResourceRole?.title || "Users";
  // Sidebar /users → "Users"; dashboard widgets / resources routes → "Resources"
  const listPageTitle =
    resourceRoleConfig || activeResourceRole ? "Resources" : "Users";
  const usersRouteBase = resourceRoleConfig?.slug
    ? `/resources/${resourceRoleConfig.slug}`
    : "/users";
  const isTrainerResourceView =
    resourceRoleConfig?.slug === "trainers" ||
    String(resourceRoleConfig?.roleName || "").toLowerCase() === "trainer";

  useEffect(() => {
    if (!organizationId || resourceRoleConfig) {
      setUserTypeOptions([]);
      setUserRoleOptions([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const [typeRes, roleRes] = await Promise.all([
          fetchUserTypes(organizationId),
          ApplicationRoles(organizationId),
        ]);
        if (cancelled) return;
        const typePayload = typeRes?.userTypes;
        const typeRows = Array.isArray(typePayload)
          ? typePayload
          : typePayload?.data || [];
        setUserTypeOptions(
          typeRows
            .map((item) => ({
              id: item?.organization_user_type_id ?? item?.id,
              name: String(item?.user_type_name || "").trim(),
            }))
            .filter((item) => item.id != null && item.name)
            .sort((a, b) => a.name.localeCompare(b.name))
        );
        const roleRows = Array.isArray(roleRes?.userroles) ? roleRes.userroles : [];
        setUserRoleOptions(
          roleRows
            .map((item) => ({
              id: item?.organization_user_role_id ?? item?.id,
              name: String(item?.user_role_name || item?.role_name || "").trim(),
            }))
            .filter((item) => item.id != null && item.name)
            .sort((a, b) => a.name.localeCompare(b.name))
        );
      } catch (err) {
        console.error("Error fetching user type and role filters:", err);
        if (!cancelled) {
          setUserTypeOptions([]);
          setUserRoleOptions([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [organizationId, resourceRoleConfig]);

  // Permission checks
  const hasAddPermission = useMemo(() => {
    return Permission && Array.isArray(Permission) && Permission.includes("USER_ADD");
  }, [Permission]);

  const hasEditPermission = useMemo(() => {
    return Permission && Array.isArray(Permission) && Permission.includes("USER_EDIT");
  }, [Permission]);

  const hasDeletePermission = useMemo(() => {
    return Permission && Array.isArray(Permission) && Permission.includes("USER_DELETE");
  }, [Permission]);

  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!organizationId) {
        setLoadingConfig(false);
        return;
      }

      if (resourceRoleConfig) {
        setTableConfig(null);
        setConfigColumns(RESOURCE_ROLE_COLUMNS);
        setLoadingConfig(false);
        return;
      }

      try {
        const datagridKey = `${USERS_DATAGRID_KEY}_${organizationId}`;
        const configRes = await fetch(`${MAIN_URL}/api/datagrid/get-by-context`, {
          method: "POST",
          headers: getApiHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify({
            datagrid_key: datagridKey,
            organization_id: organizationId,
          }),
        });

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const serverCfg =
            configResponse?.datagrid?.datagrid_default_configuration;

          if (serverCfg?.columns?.length > 0) {
            setTableConfig(serverCfg);
            setConfigColumns(mapSavedUserColumns(serverCfg.columns));
          } else {
            setTableConfig(null);
            setConfigColumns(USER_DEFAULT_COLUMNS);
          }
        } else {
          setTableConfig(null);
          setConfigColumns(USER_DEFAULT_COLUMNS);
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setTableConfig(null);
        setConfigColumns(USER_DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };
    loadTableConfiguration();
  }, [organizationId, resourceRoleConfig]);

  // Initialize URL parameters
  useEffect(() => {
    if (hasInitializedURL.current) return;

    if (resourceRoleConfig?.roleName && !hasInitializedRoleFilter.current) {
      const hasRoleFilter = searchParams.get("filter_application_user_role_id_name");
      if (!hasRoleFilter) {
        const params = buildUsersSearchParamsForRole(resourceRoleConfig.roleName);
        setSearchParams(params, { replace: true });
        hasInitializedRoleFilter.current = true;
        hasInitializedURL.current = true;
        return;
      }
      hasInitializedRoleFilter.current = true;
    }

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;
    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });
    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }
    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams, resourceRoleConfig]);

  const deleteUser = async (id) => {
    if (!hasDeletePermission) {
      setShowNotAllowed(true);
      throw new Error("Not allowed");
    }
    const currentOrgUserId = resolveOrganizationUserId(userData);
    if (
      currentOrgUserId != null &&
      String(currentOrgUserId) === String(id)
    ) {
      toast.error("You cannot delete your own account.");
      throw new Error("Not allowed");
    }

    try {
      await axios.delete(
        `${MAIN_URL}/api/organizations/${organizationId}/user/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      setUsers((prev) =>
        prev.filter((u) => u.organization_user_id !== id)
      );
      toast.success("User deleted successfully");
    } catch (err) {
      console.error(err);
      if (err.response?.status === 403) {
        toast.error(
          err.response?.data?.messages ||
            err.response?.data?.error ||
            "You cannot delete your own account."
        );
      } else if (err.response?.status === 422) {
        const validationErrors =
          err.response.data.messages ||
          err.response.data.error ||
          "Validation failed.";
        toast.error(
          Array.isArray(validationErrors)
            ? validationErrors.join(" ")
            : validationErrors
        );
      } else if (err.message !== "Not allowed") {
        toast.error("Something went wrong while deleting user.");
      }
      throw err;
    }
  };




useEffect(() => {
  const handler = setTimeout(() => {
    setDebouncedSearch(search);
  }, 500); // 500ms delay

  return () => clearTimeout(handler);
}, [search]);




  const ToggleStatus = async (userId, is_active, id) => {
    try {
      await axios.put(
        `${MAIN_URL}/api/organizations/${organizationId}/user/${userId}/toggle`,
        { is_active },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      setUsers((prev) =>
        prev.map((u, i) => (i === id ? { ...u, is_active } : u))
      );

      toast.success("User status updated.");
    } catch (error) {
      console.error(error);
      toast.error(extractToggleStatusError(error));
    }
  };

  const handleToggleActive = useCallback(
    async (row, nextActive) => {
      const userId = row?.organization_user_id;
      if (!userId || !organizationId) return;
      const currentOrgUserId = resolveOrganizationUserId(userData);
      if (
        currentOrgUserId != null &&
        String(currentOrgUserId) === String(userId) &&
        !nextActive
      ) {
        toast.error("You cannot deactivate your own account.");
        return;
      }

      try {
        await axios.put(
          `${MAIN_URL}/api/organizations/${organizationId}/user/${userId}/toggle`,
          { is_active: nextActive },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        setUsers((prev) =>
          prev.map((u) =>
            u.organization_user_id === userId
              ? { ...u, is_active: nextActive ? 1 : 0 }
              : u
          )
        );
        setPaginationData((prev) => {
          const active = Number(prev.active_users_count ?? 0);
          return {
            ...prev,
            active_users_count: nextActive
              ? active + 1
              : Math.max(active - 1, 0),
          };
        });
        toast.success(
          `User status updated to ${nextActive ? "Active" : "Inactive"}`
        );
      } catch (error) {
        toast.error(extractToggleStatusError(error));
      }
    },
    [organizationId, userData]
  );

// useEffect(() => {

//   if (!org?.organization_id) return;
//   setLoading(true);

//   const fetchUsers = async () => {
//     try {
//       const data = await getOrganizationUser(org?.organization_id, search, filterStatus);
//       console.log("data is ", data)
//       setFilteredUsers(data.users);
//     } catch (error) {
//       console.error("Failed to fetch users:", error);
//     } finally {
//       setLoading(false);
//     }
//   };
//   fetchUsers();
// }, [org?.organization_id]);



// useEffect(() => {
//   if (!org?.organization_id) return;
//   const fetchUsers = async () => {
//     try {
   
//        const data = await getOrganizationUser(org?.organization_id);
//       console.log("saysgugugugugugs", data)
//       setFilteredUsers(data?.users);
//     } catch (error) {
//       console.error("Failed to fetch users:", error);
//     }
//   };
//   fetchUsers();
// }, [search, filterStatus, org?.organization_id]);



// useEffect(() => {
//   if (!org?.organization_id) return;

//   const fetchUsers = async () => {
//     try {
//       setLoading(true);

//       const data = await getOrganizationUser(
//         org?.organization_id,
//         debouncedSearch,     // ⬅ IMPORTANT
//         filterStatus
//       );

//       setFilteredUsers(data?.users || []);
//     } catch (error) {
//       console.error("Failed to fetch users:", error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   fetchUsers();
// }, [org?.organization_id, debouncedSearch, filterStatus]);






  useEffect(() => {
    if (!organizationId) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();

    if (!hasLoadedUsersOnceRef.current) {
      setInitialLoading(true);
    } else {
      setFetching(true);
    }

    const page = Number.parseInt(searchParams.get("page"), 10) || 1;
    const pageSize = Number.parseInt(searchParams.get("pageSize"), 10) || 10;

    const fetchData = async () => {
      try {
        const filters = buildFiltersFromParams(searchParams);
        const userTypeId = String(searchParams.get("organization_user_type_id") || "").trim();
        const userRoleId = String(searchParams.get("organization_user_role_id") || "").trim();
        if (userTypeId && userTypeId.toLowerCase() !== "all") {
          filters.push({
            field: "organization_user_type_id",
            operator: "equals",
            value: userTypeId,
          });
        }
        if (userRoleId && userRoleId.toLowerCase() !== "all") {
          filters.push({
            field: "organization_user_role_id",
            operator: "equals",
            value: userRoleId,
          });
        }
        const search = searchParams.get("search");

        const requestBody = {
          filters,
          page,
          pageSize,
          sort: {
            field: resolveUserApiField(
              searchParams.get("sortBy") || "application_user_full_name"
            ),
            direction: searchParams.get("sortOrder") || "asc",
          },
          ...(search && search.trim() !== "" && { search: search.trim() }),
        };

        const response = await fetch(
          `${MAIN_URL}/api/organizations/${organizationId}/user/filtered`,
          {
            method: "POST",
            headers: getApiHeaders(),
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data;
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;

            const pag = result.pagination;
            const limit = pag?.pageSize ?? pag?.limit ?? pageSize;
            const total = pag?.total ?? 0;
            const totalPages = Math.max(
              1,
              pag?.totalPages ?? (total > 0 ? Math.ceil(total / limit) : 1)
            );

            setPaginationData({
              page: pag?.page ?? page,
              pageSize: limit,
              total,
              totalPages,
              hasNext: Boolean(pag?.hasNext ?? page < totalPages),
              hasPrev: Boolean(pag?.hasPrev ?? page > 1),
              active_users_count: Number(pag?.active_users_count ?? 0),
              total_users_count: Number(pag?.total_users_count ?? total),
            });
          } else {
            data = [];
            setPaginationData({ ...DEFAULT_PAGINATION, page, pageSize });
          }
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const mapped = (data || []).map((item) => ({
          ...item,
          id: item.organization_user_id,
        }));

        setUsers(mapped);
      } catch (err) {
        if (err.name === "AbortError") return;

        console.error("Failed to fetch users:", err);
        toast.error("Failed to fetch users");
        if (!hasLoadedUsersOnceRef.current) {
          setUsers([]);
          setPaginationData({ ...DEFAULT_PAGINATION, page, pageSize });
        }
      } finally {
        setInitialLoading(false);
        setFetching(false);
        hasLoadedUsersOnceRef.current = true;
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [organizationId, searchParams.toString()]);

  const selectedUserType = (() => {
    const raw = String(searchParams.get("organization_user_type_id") || "").trim();
    return !raw || raw.toLowerCase() === "all" ? "All" : raw;
  })();
  const selectedUserRole = (() => {
    const raw = String(searchParams.get("organization_user_role_id") || "").trim();
    return !raw || raw.toLowerCase() === "all" ? "All" : raw;
  })();
  const handleUserListFilterChange = (param) => (event) => {
    const value = String(event.target.value || "").trim();
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value && value !== "All") next.set(param, value);
      else next.delete(param);
      next.set("page", "1");
      return next;
    });
  };
  const filterSelectSx = {
    width: 240,
    minWidth: 240,
    "& .MuiSelect-select": {
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  };
  const userFilterToolbar = resourceRoleConfig ? null : (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
      <Box sx={{ width: 240, minWidth: 240, flexShrink: 0 }}>
        <TextField
          select
          size="small"
          label="User Type"
          value={selectedUserType}
          onChange={handleUserListFilterChange("organization_user_type_id")}
          sx={filterSelectSx}
        >
          <MenuItem value="All">All</MenuItem>
          {userTypeOptions.map((option) => (
            <MenuItem key={`type-${option.id}`} value={String(option.id)}>
              {option.name}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Box sx={{ width: 240, minWidth: 240, flexShrink: 0 }}>
        <TextField
          select
          size="small"
          label="User Role"
          value={selectedUserRole}
          onChange={handleUserListFilterChange("organization_user_role_id")}
          sx={filterSelectSx}
        >
          <MenuItem value="All">All</MenuItem>
          {userRoleOptions.map((option) => (
            <MenuItem key={`role-${option.id}`} value={String(option.id)}>
              {option.name}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    </Box>
  );

  const handleSortChange = useCallback(
    (field, direction) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        const currentField =
          prev.get("sortBy") || "application_user_full_name";
        const currentDirection = prev.get("sortOrder") || "asc";

        // Only reset to page 1 if the sort field/direction actually changed.
        const apiField = resolveUserApiField(field);
        if (currentField !== apiField || currentDirection !== direction) {
          next.set("sortBy", apiField);
          next.set("sortOrder", direction);
          next.set("page", "1");
        }

        return next;
      }, { replace: true });
    },
    [setSearchParams]
  );

  const handleFilterChange = useCallback(
    (filtersOrUpdates) => {
      setSearchParams((prev) => {
        const prevParams = new URLSearchParams(prev);
        const next = new URLSearchParams(prev);

        const buildFilterSignature = (params) =>
          [...params.keys()]
            .filter((k) => k.startsWith("filter_") || k === "search")
            .sort()
            .map((k) => `${k}=${params.get(k) ?? ""}`)
            .join("&");

        const prevSignature = buildFilterSignature(prevParams);

        if (Array.isArray(filtersOrUpdates)) {
          [...next.keys()]
            .filter((k) => k.startsWith("filter_"))
            .forEach((k) => next.delete(k));
          filtersOrUpdates.forEach((f) => {
            if (f?.field != null) {
              next.set(`filter_${f.field}`, f.value ?? "");
              if (f.operator) next.set(`filter_${f.field}_operator`, f.operator);
            }
          });
        } else if (
          filtersOrUpdates &&
          typeof filtersOrUpdates === "object" &&
          filtersOrUpdates.searchQuery !== undefined
        ) {
          if (String(filtersOrUpdates.searchQuery).trim() !== "") {
            next.set("search", filtersOrUpdates.searchQuery.trim());
          } else {
            next.delete("search");
          }
        }

        const nextSignature = buildFilterSignature(next);

        // Only reset to page 1 if filters or search actually changed.
        if (prevSignature !== nextSignature) {
          next.set("page", "1");
        }

        return next;
      }, { replace: true });
    },
    [setSearchParams]
  );

  const handlePageChange = useCallback(
    (page) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("page", String(page));
        if (!next.has("pageSize")) next.set("pageSize", "10");
        return next;
      }, { replace: true });
    },
    [setSearchParams]
  );

  const handleServerSearchChange = useCallback(
    (value) => {
      if (serverSearchDebounceRef.current) {
        clearTimeout(serverSearchDebounceRef.current);
      }
      serverSearchDebounceRef.current = setTimeout(() => {
        handleFilterChange({ searchQuery: value });
      }, 400);
    },
    [handleFilterChange]
  );

  useEffect(
    () => () => {
      if (serverSearchDebounceRef.current) {
        clearTimeout(serverSearchDebounceRef.current);
      }
    },
    []
  );

  // Table data: use `users` (from POST /user/filtered). Support both flat API shape and legacy nested shape.
  const tableRows = useMemo(
    () =>
      users.map((user) => {
        const mapped = resourceRoleConfig
          ? mapResourceUserRowForDisplay(user)
          : mapUserRowForDisplay(user);
        return withUserToggleGuard(mapped, paginationData, userData);
      }),
    [users, resourceRoleConfig, paginationData, userData]
  );

  const handleEdit = (row) => {
    navigate(`/users/edit/${row.organization_user_id}`);
  };

if(showNotAllowed)
{
  return <NotAllowed/>
}
  return (
    <div className="ems-fade-up">
      <div className="ems-page-head">
        <div>
          <h1 className="ems-page-title">{listPageTitle}</h1>
          <div className="ems-page-sub">
            <span>{pageSubtitle}</span>
            <span className="ems-dot" />
            <span>
              {paginationData.total} {pageCountLabel}
            </span>
          </div>
        </div>
        <div className="ems-head-actions">
          {hasAddPermission && (
            <button className="ems-btn ems-primary" onClick={() => navigate("/users/add")}>
              <Plus size={16} />
              Add User
            </button>
          )}
        </div>
      </div>

      <div className="ems-section-divider" />

      {initialLoading || loadingConfig ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
          <CircularProgress />
        </div>
      ) : (
        <TableDataGeneric
          tableName="Users"
          primaryKey="organization_user_id"
          heading={tableHeading}
          data={tableRows}
          sortname="organization_user_id"
          Route={usersRouteBase}
          DeleteFunc={isTrainerResourceView ? undefined : deleteUser}
          EditFunc={
            isTrainerResourceView || !hasEditPermission ? undefined : handleEdit
          }
          showActions={!isTrainerResourceView}
          showViewButton={!isTrainerResourceView}
          showEditButton={!isTrainerResourceView}
          showDeleteButton={!isTrainerResourceView}
          token={localStorage.getItem("token")}
          configss={configColumns}
          paginationData={paginationData}
          setPaginationData={setPaginationData}
          onSortChange={handleSortChange}
          onFilterChange={handleFilterChange}
          onPageChange={handlePageChange}
          toolbarCenterContent={userFilterToolbar}
          serverSearchEnabled
          serverSearchValue={searchParams.get("search") || ""}
          onServerSearchChange={handleServerSearchChange}
          filterModule="organization_users"
          loadedBackendConfig={resourceRoleConfig ? null : tableConfig}
          hideActiveFiltersBar={Boolean(resourceRoleConfig)}
          hideFilterButton={Boolean(resourceRoleConfig)}
          hidePersonalizeView={Boolean(resourceRoleConfig)}
          edit_delete_action={["USER_EDIT", "USER_DELETE"]}
          toggleActiveFn={
            isTrainerResourceView || !hasEditPermission
              ? undefined
              : handleToggleActive
          }
          loading={fetching || loadingConfig}
          {...(tableConfig && !resourceRoleConfig && { config: tableConfig })}
        />
      )}
    </div>
  );
}

export default Users;

